import { container } from "../../../infrastructure/container"
import { SessionService } from "../../../infrastructure/services/session"

interface CreateButtonProps {
  /**
  * Unique identifier for the button.
  */
  id: string

  /**
   * Function to update the visibility of the user creation form.
   */
  setShowForm: (value: boolean) => void

  /**
   * Text to display on the button.
   */
  buttonText: string
}

/**
 * @component CreateButton
 * @description A button component to show the user creation form. It is only visible if the user has permission to create users.
 *
 * @param {CreateButtonProps} props - Component props.
 * @param {boolean} props.showForm - Determines if the form is visible.
 * @param {(value: boolean) => void} props.setShowForm - Function to toggle form visibility.
 * @param {string} props.buttonText - Text to display on the button.
 *
 * @returns {JSX.Element | null} A button to show the user creation form or null if the user lacks permission.
 */
const CreateButton: React.FC<CreateButtonProps> = ({
  id,
  setShowForm,
  buttonText,
}) => {
  const appSessionService = container.resolve<SessionService>("sessionService")

  if (!appSessionService.canCreateUser()) {
    return null
  }

  return (
    <button
      data-automation-id={id}
      id={id}
      className="bg-blue-500 text-white px-4 py-2 rounded"
      onClick={() => setShowForm(true)}
    >
      {buttonText}
    </button>
  )
}

export default CreateButton
